/**
 * "Something that feels like leaving."
 *
 * Optional, and it has to *look* optional. An intention makes the branch sets noticeably
 * better, which is exactly the pressure that turns a nice field into a required one — and
 * a required field on the first screen would make the app a form, which is the specific
 * failure the brief names. So: no asterisk, no validation, placeholder text that shows the
 * register rather than instructing, and a "Start playing" button that is fully enabled
 * with the field empty.
 */

import { useId } from 'react';

/** Rotated so the field never looks like it wants one particular answer. */
const PLACEHOLDERS: readonly string[] = Object.freeze([
  'something that feels like leaving',
  'warm, then suddenly not',
  'a slow walk downhill',
  'unresolved on purpose',
]);

export interface IntentionInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  /** Index into the placeholder list; injected so tests and fixtures are deterministic. */
  placeholderSeed?: number;
}

export function IntentionInput({
  value,
  onChange,
  onSubmit,
  placeholderSeed = 0,
}: IntentionInputProps): React.JSX.Element {
  const id = useId();
  const placeholder = PLACEHOLDERS[placeholderSeed % PLACEHOLDERS.length];

  return (
    <div className="hc-intention">
      <label className="hc-intention__label" htmlFor={id}>
        Anything in mind? <span className="hc-intention__optional">optional</span>
      </label>
      <input
        id={id}
        className="hc-intention__input"
        data-testid="hc-intention-input"
        type="text"
        value={value}
        placeholder={placeholder}
        maxLength={500}
        autoComplete="off"
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && onSubmit) {
            event.preventDefault();
            onSubmit();
          }
        }}
      />
    </div>
  );
}
